import type { PointerSide } from "./tutorialSteps";
import type { TargetRect } from "./useTargetRect";

export interface BubblePos {
  top: number;
  left: number;
}

export interface Viewport {
  width: number;
  height: number;
}

const GAP = 12;
const MARGIN = 16;

const ALL_SIDES: readonly PointerSide[] = ["bottom", "top", "right", "left"];

function clamp(value: number, size: number, viewport: number): number {
  const max = Math.max(MARGIN, viewport - size - MARGIN);
  return Math.min(Math.max(value, MARGIN), max);
}

/** Free space between the target edge and the matching viewport edge. */
function clearance(side: PointerSide, rect: TargetRect, vp: Viewport): number {
  switch (side) {
    case "top":
      return rect.top;
    case "bottom":
      return vp.height - (rect.top + rect.height);
    case "left":
      return rect.left;
    case "right":
      return vp.width - (rect.left + rect.width);
  }
}

/** Whether the bubble fully fits beside the target on this side without overflow. */
function sideFits(
  side: PointerSide,
  rect: TargetRect,
  width: number,
  height: number,
  vp: Viewport,
): boolean {
  const needed = side === "top" || side === "bottom" ? height : width;
  return clearance(side, rect, vp) >= needed + GAP + MARGIN;
}

function positionOn(
  side: PointerSide,
  rect: TargetRect,
  width: number,
  height: number,
  vp: Viewport,
): BubblePos {
  switch (side) {
    case "top":
      return { top: rect.top - GAP - height, left: clamp(rect.left, width, vp.width) };
    case "bottom":
      return { top: rect.top + rect.height + GAP, left: clamp(rect.left, width, vp.width) };
    case "left":
      return { top: clamp(rect.top, height, vp.height), left: rect.left - GAP - width };
    case "right":
      return { top: clamp(rect.top, height, vp.height), left: rect.left + rect.width + GAP };
  }
}

/**
 * Places the coachmark beside its target without covering it. The preferred
 * side is used when the bubble fully fits there; otherwise the side with the
 * most clearance that fits is chosen, so the bubble lands in open space rather
 * than over neighbouring content. If nothing fits (tiny viewport) it falls back
 * to the roomiest side and clamps. Centered when there is no target.
 */
export function computeBubblePosition(
  rect: TargetRect | null,
  preferredSide: PointerSide,
  bubbleWidth: number,
  bubbleHeight: number,
  vp: Viewport,
): BubblePos {
  if (!rect) {
    return {
      top: Math.round((vp.height - bubbleHeight) / 2),
      left: Math.round((vp.width - bubbleWidth) / 2),
    };
  }

  const byClearance = [...ALL_SIDES].sort(
    (a, b) => clearance(b, rect, vp) - clearance(a, rect, vp),
  );
  const order = [preferredSide, ...byClearance.filter((side) => side !== preferredSide)];

  const chosen: PointerSide =
    order.find((side) => sideFits(side, rect, bubbleWidth, bubbleHeight, vp)) ??
    byClearance[0] ??
    preferredSide;

  const pos = positionOn(chosen, rect, bubbleWidth, bubbleHeight, vp);
  return {
    top: Math.round(clamp(pos.top, bubbleHeight, vp.height)),
    left: Math.round(clamp(pos.left, bubbleWidth, vp.width)),
  };
}
